// Thunk for async actions related to soft tags
import { createAsyncThunk } from '@reduxjs/toolkit';
import { isAxiosError } from 'axios';
import {
  getSearchTagList,
  getSoftTagAPI,
  getSoftTagsList,
  uploadBulkImport,
} from '../api/tags.api';
import type {
  CreateSearchTagParams,
  GetSoftTagParams,
  SoftTagsParams,
  UploadBulkImportRejectPayload,
  UploadBulkImportSuccessPayload,
} from '../types/tags.types';
import { setResponseErrorStatus } from '@/features/session/store/sessionSlice';
import {
  csvHasInvalidRows,
  extractCsvResponse,
} from '../utils/bulk.import.helpers';

// Async thunk to fetch the list of soft tags from the API
// Accepts optional params for pagination, sorting, and filtering
// If params are not provided, uses default values
// On success, returns the API response
// On failure, rejects with the error value
export const getSoftTags = createAsyncThunk(
  'Tags/getSoftTags',
  async (params: SoftTagsParams | undefined, thunkAPI) => {
    try {
      const defaultParams: SoftTagsParams = {
        size: 10,
        page: 1,
        sort_by: 'name',
        sort_order: 'asc',
      };
      const response = await getSoftTagsList(params ?? defaultParams);
      return response;
    } catch (e) {
      if (isAxiosError(e)) {
        const status = e.response?.status;
        if (status === 401 || status === 403 || status === 404) {
          thunkAPI.dispatch(setResponseErrorStatus(status));
        }
      }
      return thunkAPI.rejectWithValue('Error fetching soft tags');
    }
  }
);

export const getSearchTags = createAsyncThunk(
  'Tags/getSearchTags',
  async (params: CreateSearchTagParams | undefined, thunkAPI) => {
    try {
      const response = await getSearchTagList(params);
      return response;
    } catch (e) {
      // Return error if API call fails
      return thunkAPI.rejectWithValue(e);
    }
  }
);

export const getSoftTag = createAsyncThunk(
  'Tags/getSoftTag',
  async (params: GetSoftTagParams | undefined, thunkAPI) => {
    try {
      const response = await getSoftTagAPI(params);
      return response;
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  }
);

export const uploadBulkImportFile = createAsyncThunk<
  UploadBulkImportSuccessPayload,
  FormData,
  { rejectValue: UploadBulkImportRejectPayload }
>('Tags/uploadBulkImportFile', async (formData: FormData, thunkAPI) => {
  try {
    const response = await uploadBulkImport(formData);
    const responseCsv = await extractCsvResponse(response);

    return {
      response,
      responseCsv,
      hasValidationErrors: responseCsv ? csvHasInvalidRows(responseCsv) : false,
    };
  } catch (e) {
    if (isAxiosError(e)) {
      const responseData = e.response?.data;
      const contentTypeHeader = e.response?.headers?.['content-type'];
      const contentType =
        typeof contentTypeHeader === 'string' ? contentTypeHeader : '';

      const errorCsv = await extractCsvResponse(responseData, contentType);

      return thunkAPI.rejectWithValue({
        message: e.message || 'Failed to upload bulk import file',
        errorCsv,
        status: e.response?.status,
      });
    }

    return thunkAPI.rejectWithValue({
      message: 'Failed to upload bulk import file',
      errorCsv: null,
    });
  }
});
